/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosElementDownloadStep, DownloadableSectionFragment } from "@cosmos/hygraph-schema";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import {
	DownloadFooterCard,
	ResponsiveContainer,
	SectionHeading,
	BackgroundContainer,
	SectionContainer,
} from "@components/elements";
import { useDownloadLauncher } from "@components/hooks";
import { VStack } from "@panda/jsx";

import { DownloadSteps } from "../download-cards/DownloadSteps.organism";

import { DownloadableCards } from "./DownloadableCards.organism";
import { getDownloadBtnFromSteps } from "./downloadable.utils";

export const DownloadableSection = ({
	config,
	instructions,
	executables,
	footer,
}: DownloadableSectionFragment): React.JSX.Element | null => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const [windowsUrl, setWindowsUrl] = useState("");
	const [macUrl, setMacUrl] = useState("");
	const steps = useMemo(() => {
		return instructions?.section?.__typename === "CosmosSectionDownloadCard" ? instructions?.section?.steps : [];
	}, [instructions]);

	useEffect(() => {
		const downloadBtnContent = getDownloadBtnFromSteps(steps as CosmosElementDownloadStep[]);
		if (downloadBtnContent) {
			if (downloadBtnContent?.windowsUrl) setWindowsUrl(downloadBtnContent?.windowsUrl);
			if (downloadBtnContent?.macUrl) setMacUrl(downloadBtnContent?.macUrl);
		}
	}, [steps]);

	const downloadLauncher = useDownloadLauncher(windowsUrl, macUrl);

	// Since DownloadableSection will only be used on /download, start the download if ?start=true
	useEffect(() => {
		if (searchParams && searchParams.has("start")) {
			if (windowsUrl || macUrl) {
				downloadLauncher();
				const newSearchParams = new URLSearchParams(searchParams.toString());
				newSearchParams.delete("start");
				router.replace(pathname + "?" + newSearchParams.toString());
			}
		}
	}, [downloadLauncher, macUrl, pathname, router, searchParams, windowsUrl]);
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(
		steps?.length || executables?.length || footer?.title || footer?.titleIcon || footer?.description || footer?.cta,
	);
	if (!validHeading && !validContent) return null;

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer
					disablePaddingBlockStart={!steps?.length}
					disablePaddingBlockEnd={!!config?.disablePaddingBottom}
				>
					<VStack gap="eds.dimension.48" alignItems="stretch" className="downloadable-section">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<VStack gap="eds.dimension.64" alignItems="stretch">
								<VStack gap="eds.dimension.16" alignItems="stretch">
									{steps?.length ? <DownloadSteps steps={steps || []} /> : null}
									{footer ? <DownloadFooterCard card={footer} /> : null}
								</VStack>
								{executables?.length ? <DownloadableCards cards={executables || []} /> : null}
							</VStack>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
